import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { T, t, FormattedDateTime } from '@sm/intl';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Modal } from '@wds/modal';
import CancelSeatRequestMutation from './graphql/CancelSeatRequest.graphql';
import RemindSeatRequestMutation from './graphql/RemindSeatRequest.graphql';

import COPY from './SeatRequestCopy';
import { STATUS } from './constants';

const PendingModalContent = ({ onModalFinished, seatRequest, team }) => {
  const [cancelSeatRequest, { loading: cancelLoading }] = useMutation(
    CancelSeatRequestMutation,
    {
      onError: () => {
        onModalFinished({
          status: STATUS.FAILURE,
          message: t(COPY.TOAST_ERROR),
        });
      },
      onCompleted: () => {
        onModalFinished({
          status: STATUS.SUCCESS,
          message: t(COPY.TOAST_REQUEST_CANCELED),
        });
      },
    }
  );
  const [remindSeatRequest, { loading: remindLoading }] = useMutation(
    RemindSeatRequestMutation,
    {
      onError: () => {
        onModalFinished({
          status: STATUS.FAILURE,
          message: t(COPY.TOAST_ERROR),
        });
      },
      onCompleted: () => {
        onModalFinished({
          status: STATUS.SUCCESS,
          message: t(COPY.TOAST_REMINDER_SENT),
        });
      },
    }
  );

  const { email, firstName, lastName } = team.accountOwner;
  const emailLabel = firstName
    ? `${firstName}${lastName ? `\u00A0${lastName}` : ''}`
    : email;
  return (
    <>
      <Modal.Header header={<T desc={COPY.MODAL_TITLE_PENDING} />} />
      <Modal.Body>
        <p>
          <T
            desc={COPY.PENDING_DESC_1}
            values={{
              date: FormattedDateTime({
                date: new Date(seatRequest.request.date),
              }),
            }}
          />
        </p>
        <p>
          <T desc={COPY.PENDING_DESC_2} />
        </p>
        <p>
          <T
            desc={COPY.PENDING_DESC_3}
            values={{
              email: () => <a href={`mailto:${email}`}>{emailLabel}</a>,
            }}
          />
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Box
          flexDirection="row"
          display="flex"
          flexJustify="space-between"
          flexItemGrow={1}
        >
          <div>
            <Button
              variant="ghost"
              color="warning"
              onClick={cancelSeatRequest}
              disabled={remindLoading || cancelLoading}
            >
              <T desc={COPY.CANCEL_REQUEST_BUTTON} />
            </Button>
          </div>
          <div>
            <Button
              onClick={remindSeatRequest}
              color="secondary"
              disabled={remindLoading || cancelLoading}
            >
              <T desc={COPY.SEND_REMINDER_BUTTON} />
            </Button>
            &nbsp;
            <Button
              onClick={onModalFinished}
              disabled={remindLoading || cancelLoading}
            >
              <T desc={COPY.CLOSE_BUTTON} />
            </Button>
          </div>
        </Box>
      </Modal.Footer>
    </>
  );
};

PendingModalContent.propTypes = {
  onModalFinished: PropTypes.func.isRequired,
  seatRequest: PropTypes.shape({
    request: PropTypes.shape({
      date: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  team: PropTypes.shape({
    accountOwner: PropTypes.shape({
      email: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default PendingModalContent;
